import { useState, useCallback, useRef, useEffect } from 'react'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useWorkspaceStore } from '@/store/workspaceStore'
import { ProjectGraph } from '../ProjectGraph'
import { SankeyView } from '../center/SankeyView'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { GitBranch, Workflow, Loader2, Database, Maximize2, Minimize2 } from 'lucide-react'
import type { ExchangeType, DraggedComponentData, ApiRequestError } from '@/lib/types'

const EXCHANGE_TYPE_OPTIONS: { value: ExchangeType; label: string }[] = [
  { value: 'technosphere', label: 'Technosphere (input from another activity)' },
  { value: 'biosphere', label: 'Biosphere (environmental flow)' },
  { value: 'production', label: 'Production (output of this activity)' },
  { value: 'substitution', label: 'Substitution (avoided burden)' },
]
import { toast } from 'sonner'
import { projectAPI } from '@/lib/api/projects'
import type { ActivityWithExchanges } from '@/lib/types/project-graph'

interface CenterPanelProps {
  activities: ActivityWithExchanges[]
  projectName?: string
  projectDatabase?: string
  selectedNodeId?: string | null
  selectedNodeType?: 'activity' | 'exchange' | null
  onNodeClick: (nodeId: string, nodeType: 'activity' | 'exchange') => void
  onExchangeCreated?: () => void
  isLoading?: boolean
}

interface DropDialogState {
  open: boolean
  activityId: string
  activityName: string
  dragData: DraggedComponentData | null
}

export function CenterPanel({
  activities,
  projectName,
  projectDatabase,
  selectedNodeId,
  selectedNodeType,
  onNodeClick,
  onExchangeCreated,
  isLoading,
}: CenterPanelProps) {
  const { centerTab, setCenterTab } = useWorkspaceStore()
  const [dropDialog, setDropDialog] = useState<DropDialogState>({
    open: false,
    activityId: '',
    activityName: '',
    dragData: null,
  })
  const [amount, setAmount] = useState('1')
  const [exchangeType, setExchangeType] = useState<ExchangeType>('technosphere')
  const [creating, setCreating] = useState(false)

  // Fullscreen for the center panel — covers both Model and Hotspots tabs
  // because they live inside the same root. We use the browser Fullscreen
  // API on the root div so the user keeps the tab switcher while expanded.
  const rootRef = useRef<HTMLDivElement>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const toggleFullscreen = useCallback(() => {
    const el = rootRef.current
    if (!el) return
    if (!document.fullscreenElement) {
      el.requestFullscreen?.().catch(() => {
        /* user-rejected or unsupported */
      })
    } else {
      document.exitFullscreen?.().catch(() => {})
    }
  }, [])
  useEffect(() => {
    const handler = () => setIsFullscreen(document.fullscreenElement === rootRef.current)
    document.addEventListener('fullscreenchange', handler)
    return () => document.removeEventListener('fullscreenchange', handler)
  }, [])

  const handleDropOnActivity = useCallback(
    (activityId: string, dragData: DraggedComponentData) => {
      // Find the activity name for display
      const activity = activities.find((a) => a.id === activityId)
      if (!activity) return

      // Fail-fast: drop payloads must declare an exchange_type. The drag source
      // (ComponentLibrary, etc.) is responsible for setting a sane default; we
      // do NOT silently fall back to technosphere here.
      const payloadType = dragData?.exchange_type
      if (!payloadType || !EXCHANGE_TYPE_OPTIONS.some((o) => o.value === payloadType)) {
        toast.error(
          `Drop payload missing or invalid exchange_type (got ${JSON.stringify(payloadType)}). ` +
            `The drag source must declare one of: ${EXCHANGE_TYPE_OPTIONS.map((o) => o.value).join(', ')}.`,
        )
        return
      }

      setAmount('1')
      setExchangeType(payloadType)
      setDropDialog({
        open: true,
        activityId,
        activityName: activity.name,
        dragData: { ...dragData, exchange_type: payloadType },
      })
    },
    [activities],
  )

  const handleCreateExchange = async () => {
    if (!dropDialog.dragData || !projectDatabase) return
    const numAmount = parseFloat(amount)
    if (isNaN(numAmount) || numAmount <= 0) {
      toast.error('Amount must be a positive number')
      return
    }

    setCreating(true)
    try {
      await projectAPI.createEcoinventExchange(dropDialog.activityId, {
        amount: numAmount,
        exchange_type: exchangeType,
        input_database: dropDialog.dragData.database,
        input_activity_code: dropDialog.dragData.code,
      })
      toast.success(`Exchange added: ${dropDialog.dragData.name}`)
      setDropDialog((prev) => ({ ...prev, open: false }))
      onExchangeCreated?.()
    } catch (err) {
      const detail = (err as ApiRequestError)?.response?.data?.detail
      toast.error(
        typeof detail === 'string' ? detail : detail?.message || 'Failed to create exchange',
      )
    } finally {
      setCreating(false)
    }
  }

  return (
    <div ref={rootRef} className="h-full flex flex-col bg-background min-h-0">
      <Tabs
        value={centerTab}
        onValueChange={(v) => setCenterTab(v as 'graph' | 'sankey')}
        className="flex-1 flex flex-col min-h-0"
      >
        {/* Header with tabs and project info */}
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-border bg-surface-sunken/50">
          <div className="flex items-center gap-3">
            <TabsList className="h-8 p-0.5 bg-muted/50">
              <TabsTrigger
                value="graph"
                className="h-7 px-2.5 text-xs gap-1.5 data-[state=active]:bg-background"
              >
                <GitBranch className="h-3.5 w-3.5" />
                Model
              </TabsTrigger>
              <TabsTrigger
                value="sankey"
                className="h-7 px-2.5 text-xs gap-1.5 data-[state=active]:bg-background"
              >
                <Workflow className="h-3.5 w-3.5" />
                Hotspots
              </TabsTrigger>
            </TabsList>

            {/* Project info */}
            {projectName && (
              <div className="text-xs text-muted-foreground border-l border-border pl-3">
                <span className="font-medium text-foreground">{projectName}</span>
                {projectDatabase && (
                  <span className="ml-2 text-muted-foreground">
                    {projectDatabase} &bull; {activities.length} activities
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Loading indicator */}
            {isLoading && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Updating...
              </div>
            )}
            <button
              type="button"
              onClick={toggleFullscreen}
              className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-border bg-background hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
              title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Enter fullscreen'}
              aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            >
              {isFullscreen ? (
                <Minimize2 className="h-3.5 w-3.5" />
              ) : (
                <Maximize2 className="h-3.5 w-3.5" />
              )}
            </button>
          </div>
        </div>

        {/* All tab panels always mounted — hidden via CSS to preserve state */}
        <div className={centerTab === 'graph' ? 'flex-1 overflow-hidden min-h-0' : 'hidden'}>
          {activities.length > 0 ? (
            <ProjectGraph
              activities={activities}
              selectedNodeId={selectedNodeId}
              selectedNodeType={selectedNodeType}
              onNodeClick={onNodeClick}
              onDropOnActivity={handleDropOnActivity}
              onGraphMutated={onExchangeCreated}
            />
          ) : (
            <EmptyState />
          )}
        </div>

        <div className={centerTab === 'sankey' ? 'flex-1 overflow-hidden min-h-0' : 'hidden'}>
          <SankeyView
            activities={activities}
            projectName={projectName}
            projectDatabase={projectDatabase}
            selectedNodeId={selectedNodeId}
            onNodeClick={onNodeClick}
          />
        </div>
      </Tabs>

      {/* Drop Exchange Dialog */}
      <Dialog
        open={dropDialog.open}
        onOpenChange={(open) => setDropDialog((prev) => ({ ...prev, open }))}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sm">Add Exchange</DialogTitle>
          </DialogHeader>
          {dropDialog.dragData && (
            <div className="space-y-4">
              <div className="space-y-2 text-xs">
                <div className="p-2.5 rounded-md bg-muted/50 border border-border">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Database className="h-3 w-3 text-muted-foreground" />
                    <span className="font-medium">{dropDialog.dragData.name}</span>
                  </div>
                  <div className="text-muted-foreground">
                    {dropDialog.dragData.location} &bull; {dropDialog.dragData.unit}
                    {dropDialog.dragData.reference_product && (
                      <> &bull; {dropDialog.dragData.reference_product}</>
                    )}
                  </div>
                </div>
                <div className="text-muted-foreground">
                  Adding to:{' '}
                  <span className="text-foreground font-medium">{dropDialog.activityName}</span>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium">Exchange type</label>
                <Select
                  value={exchangeType}
                  onValueChange={(v) => setExchangeType(v as ExchangeType)}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXCHANGE_TYPE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value} className="text-xs">
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium">Amount ({dropDialog.dragData.unit})</label>
                <Input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreateExchange()
                  }}
                  className="h-8 text-sm"
                  min="0"
                  step="any"
                  autoFocus
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDropDialog((prev) => ({ ...prev, open: false }))}
            >
              Cancel
            </Button>
            <Button size="sm" onClick={handleCreateExchange} disabled={creating}>
              {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
              Add Exchange
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function EmptyState() {
  return (
    <div className="flex items-center justify-center h-full text-center text-muted-foreground p-8">
      <div>
        <GitBranch className="w-16 h-16 mx-auto mb-4 opacity-20" />
        <p className="text-lg font-medium">No Project Selected</p>
        <p className="text-sm mt-2">
          Select a project from the explorer to visualize its activities and exchanges.
        </p>
      </div>
    </div>
  )
}
